// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { CarePlan, Extension, Identifier, MedicationRequest, ResourceType } from '@medplum/fhirtypes';
import { RPM_CARE_PLAN_CATEGORY_SYSTEM } from '../rpm/rpm-enrollment';
import { withTenantScope } from '../utils/tenant';

export const TREATMENT_CARE_PLAN_CATEGORY_SYSTEM = RPM_CARE_PLAN_CATEGORY_SYSTEM;
export const TREATMENT_CARE_PLAN_CATEGORY_CODE = 'treatment';
export const TREATMENT_MODALITY_SYSTEM = 'http://polarissleep.com/treatment-modality';
/** Links a DME treatment to the therapy MedicationRequest it came from; also the idempotency key for DME upserts. */
export const TREATMENT_ORDER_IDENTIFIER_SYSTEM = 'http://polarissleep.com/medication-request';
/** Idempotency key for treatments migrated from the legacy Patient `patient-treatment` extension. */
export const TREATMENT_LEGACY_KEY_IDENTIFIER_SYSTEM = 'http://polarissleep.com/legacy-patient-treatment';
/** Coding system on device-prescription MedicationRequests; each such order is a DME treatment. */
export const THERAPY_ORDER_SYSTEM = 'http://polarissleep.com/therapies';

export const TREATMENT_CARE_PLAN_EXTENSION_URLS = {
  source: 'https://polarissleep.com/fhir/StructureDefinition/treatment-source',
  endedBy: 'https://polarissleep.com/fhir/StructureDefinition/treatment-ended-by',
} as const;

export interface PatientTreatment {
  id: string;
  modality: string;
  start: string;
  end?: string;
  other?: string;
  addedBy?: string;
  addedByName?: string;
  endedBy?: string;
  source?: 'manual' | 'dme';
  orderId?: string;
  orderName?: string;
}

export interface BuildTreatmentCarePlanOptions {
  treatment: Omit<PatientTreatment, 'id'>;
  patientId: string;
  orgId?: string;
  existing?: CarePlan;
  legacyKey?: string;
  /** Local calendar date (YYYY-MM-DD) used to derive status from `end`. */
  today: string;
}

function modalityCode(modality: string): string {
  return modality
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function hasCoding(carePlan: CarePlan, system: string, code?: string): boolean {
  return (
    carePlan.category?.some((category) =>
      category.coding?.some((coding) => coding.system === system && (code === undefined || coding.code === code))
    ) ?? false
  );
}

export function isTreatmentCarePlan(carePlan: CarePlan | undefined): boolean {
  return carePlan ? hasCoding(carePlan, TREATMENT_CARE_PLAN_CATEGORY_SYSTEM, TREATMENT_CARE_PLAN_CATEGORY_CODE) : false;
}

/**
 * A treatment with an end date of today or earlier is `completed`; otherwise it is `active`. The queue relies on
 * `status` to drop ended treatments (see {@link activeTreatmentSearchParams}), so ending a treatment completes it at
 * once. End dates are capped at today in the UI, so no saved treatment has a future end.
 */
export function treatmentStatusForEnd(end: string | undefined, today: string): CarePlan['status'] {
  return end && end <= today ? 'completed' : 'active';
}

/**
 * Order statuses under which a therapy order is a treatment; draft, on-hold, cancelled, entered-in-error and unknown
 * are not.
 */
export const TREATMENT_ORDER_STATUSES: readonly MedicationRequest['status'][] = ['active', 'completed', 'stopped'];

/** Whether the order is a device prescription, whatever its status. */
export function isTherapyOrder(order: MedicationRequest): boolean {
  return order.medicationCodeableConcept?.coding?.some((coding) => coding.system === THERAPY_ORDER_SYSTEM) ?? false;
}

/** Whether the order is a therapy order whose status makes it a DME treatment. */
export function isTreatmentOrder(order: MedicationRequest): boolean {
  return isTherapyOrder(order) && TREATMENT_ORDER_STATUSES.includes(order.status);
}

export function getTherapyOrderName(order: MedicationRequest): string {
  const coding = order.medicationCodeableConcept?.coding?.find((item) => item.system === THERAPY_ORDER_SYSTEM);
  return order.medicationCodeableConcept?.text ?? coding?.display ?? coding?.code ?? 'DME';
}

/**
 * Start date (YYYY-MM-DD) of the treatment a therapy order represents: its `authoredOn`, else when it was created.
 * Older production orders (the "APAP Therapy" form) were saved without `authoredOn` (SLE-802). Pass `createdAt`, the
 * order's first-version timestamp, when known; otherwise `meta.lastUpdated` is used, which equals creation for an
 * order that was never edited.
 */
export function therapyOrderStartDate(order: MedicationRequest, createdAt?: string): string | undefined {
  return (order.authoredOn ?? createdAt ?? order.meta?.lastUpdated)?.slice(0, 10);
}

/**
 * The DME treatment for a therapy order, starting on {@link therapyOrderStartDate}; undefined without an id or any
 * date to start from.
 */
export function therapyOrderTreatment(
  order: MedicationRequest,
  createdAt?: string
): Omit<PatientTreatment, 'id'> | undefined {
  const start = therapyOrderStartDate(order, createdAt);
  if (!order.id || !start) {
    return undefined;
  }
  return {
    modality: 'DME',
    start,
    source: 'dme',
    orderId: order.id,
    orderName: getTherapyOrderName(order),
  };
}

/** The DME treatment for an order whose dates a provider overrode on the legacy Patient `patient-treatment` extension. */
export function legacyDmeTreatment(override: PatientTreatment, order: MedicationRequest): Omit<PatientTreatment, 'id'> {
  const { id: _id, addedByName: _name, ...rest } = override;
  return { ...rest, modality: 'DME', source: 'dme', orderId: order.id, orderName: getTherapyOrderName(order) };
}

const HISTORY_PAGE_SIZE = 100;

/**
 * When a resource was created: the `meta.lastUpdated` of its first history version. Medplum returns history newest
 * first, so this pages to the end.
 */
export async function getResourceCreatedAt(
  medplum: MedplumClient,
  resourceType: ResourceType,
  id: string
): Promise<string | undefined> {
  let oldest: string | undefined;
  for (let offset = 0; ; offset += HISTORY_PAGE_SIZE) {
    const entries = (await medplum.readHistory(resourceType, id, { count: HISTORY_PAGE_SIZE, offset })).entry ?? [];
    oldest = entries[entries.length - 1]?.resource?.meta?.lastUpdated ?? oldest;
    if (entries.length < HISTORY_PAGE_SIZE) {
      return oldest;
    }
  }
}

export function getTreatmentOrderId(carePlan: CarePlan): string | undefined {
  return carePlan.identifier?.find((identifier) => identifier.system === TREATMENT_ORDER_IDENTIFIER_SYSTEM)?.value;
}

export function getTreatmentLegacyKey(carePlan: CarePlan | undefined): string | undefined {
  return carePlan?.identifier?.find((identifier) => identifier.system === TREATMENT_LEGACY_KEY_IDENTIFIER_SYSTEM)
    ?.value;
}

/**
 * Stable keys for treatments stored in the legacy Patient `patient-treatment` extension, which have no IDs of their
 * own. Lets the backfill and the profile's legacy fallback recognise a treatment that already has a CarePlan. Pass the
 * patient's manual legacy treatments in extension order: the first with a given start and modality gets
 * `patientId:start:modality`, and later ones `:2`, `:3`… Nothing writes the extension any more, so the order is fixed.
 */
export function legacyTreatmentKeys(
  patientId: string,
  treatments: Pick<PatientTreatment, 'start' | 'modality'>[]
): string[] {
  const seen = new Map<string, number>();
  return treatments.map((treatment) => {
    const key = `${patientId}:${treatment.start}:${treatment.modality}`;
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);
    return count === 1 ? key : `${key}:${count}`;
  });
}

export function buildTreatmentCarePlan({
  treatment,
  patientId,
  orgId,
  existing,
  legacyKey,
  today,
}: BuildTreatmentCarePlanOptions): CarePlan {
  const legacy = legacyKey ?? getTreatmentLegacyKey(existing);
  const identifier: Identifier[] = [
    ...(existing?.identifier ?? []).filter(
      (item) =>
        item.system !== TREATMENT_ORDER_IDENTIFIER_SYSTEM && item.system !== TREATMENT_LEGACY_KEY_IDENTIFIER_SYSTEM
    ),
    ...(treatment.orderId ? [{ system: TREATMENT_ORDER_IDENTIFIER_SYSTEM, value: treatment.orderId }] : []),
    ...(legacy ? [{ system: TREATMENT_LEGACY_KEY_IDENTIFIER_SYSTEM, value: legacy }] : []),
  ];
  const extension: Extension[] = [
    ...(existing?.extension ?? []).filter(
      (item) =>
        item.url !== TREATMENT_CARE_PLAN_EXTENSION_URLS.source &&
        item.url !== TREATMENT_CARE_PLAN_EXTENSION_URLS.endedBy
    ),
    ...(treatment.source ? [{ url: TREATMENT_CARE_PLAN_EXTENSION_URLS.source, valueCode: treatment.source }] : []),
    ...(treatment.endedBy
      ? [{ url: TREATMENT_CARE_PLAN_EXTENSION_URLS.endedBy, valueReference: { reference: treatment.endedBy } }]
      : []),
  ];
  const carePlan: CarePlan = {
    ...(existing ?? { resourceType: 'CarePlan' }),
    // A treatment voided with its order stays voided; only the bot restores it, when the order is restored.
    status: existing?.status === 'entered-in-error' ? 'entered-in-error' : treatmentStatusForEnd(treatment.end, today),
    intent: existing?.intent ?? 'plan',
    subject: { reference: `Patient/${patientId}` },
    period: { start: treatment.start, ...(treatment.end ? { end: treatment.end } : {}) },
    title: treatment.orderName ?? treatment.modality,
    description: treatment.other,
    author: treatment.addedBy ? { reference: treatment.addedBy } : undefined,
    category: [
      {
        coding: [
          {
            system: TREATMENT_CARE_PLAN_CATEGORY_SYSTEM,
            code: TREATMENT_CARE_PLAN_CATEGORY_CODE,
            display: 'Treatment',
          },
        ],
      },
      {
        coding: [
          { system: TREATMENT_MODALITY_SYSTEM, code: modalityCode(treatment.modality), display: treatment.modality },
        ],
      },
    ],
    identifier: identifier.length > 0 ? identifier : undefined,
    extension: extension.length > 0 ? extension : undefined,
  };
  return withTenantScope(carePlan, orgId);
}

/** Returns undefined for CarePlans that are not treatments or have no start date. */
export function carePlanToTreatment(carePlan: CarePlan): PatientTreatment | undefined {
  const start = carePlan.period?.start;
  if (!isTreatmentCarePlan(carePlan) || !start) {
    return undefined;
  }
  const modality =
    carePlan.category
      ?.flatMap((category) => category.coding ?? [])
      .find((coding) => coding.system === TREATMENT_MODALITY_SYSTEM)?.display ?? 'Other';
  const source = carePlan.extension?.find((item) => item.url === TREATMENT_CARE_PLAN_EXTENSION_URLS.source)
    ?.valueCode as PatientTreatment['source'];
  const orderId = getTreatmentOrderId(carePlan);
  return {
    id: carePlan.id ?? '',
    modality,
    start: start.slice(0, 10),
    end: carePlan.period?.end?.slice(0, 10),
    other: carePlan.description,
    addedBy: carePlan.author?.reference,
    endedBy: carePlan.extension?.find((item) => item.url === TREATMENT_CARE_PLAN_EXTENSION_URLS.endedBy)?.valueReference
      ?.reference,
    source,
    orderId,
    orderName: orderId && carePlan.title !== modality ? carePlan.title : undefined,
  };
}

/**
 * Search params for treatments in progress on `today` (YYYY-MM-DD): `active` and started on or before today.
 *
 * The Medplum server indexes CarePlan `date` (period) by `period.start` only, so `date=ge<today>` would exclude every
 * open-ended treatment that started earlier (verified on staging, SLE-802). Ended treatments are excluded by
 * `status` instead, which {@link treatmentStatusForEnd} sets to `completed` whenever an end date is recorded.
 */
export function activeTreatmentSearchParams(today: string): URLSearchParams {
  return new URLSearchParams([
    ['category', `${TREATMENT_CARE_PLAN_CATEGORY_SYSTEM}|${TREATMENT_CARE_PLAN_CATEGORY_CODE}`],
    ['status', 'active'],
    ['date', `le${today}`],
  ]);
}
